/**
 * Sentido de un monto de dinero, como en una app financiera.
 *
 * - `ingreso`: lo que es a favor (cobrado, entró, pagado, adelantos recibidos). Verde con flecha
 *   hacia arriba.
 * - `adeudo`: lo que es en contra (vencido, por cobrar, falta para la meta, saldo pendiente,
 *   morosidad). Rojo con flecha hacia abajo.
 * - `neutro`: montos de referencia (cartera, precio final, programado, por vencer). Sin flecha ni
 *   color.
 *
 * Un monto en 0 (en centavos) no lleva flecha y va en gris: no hay nada que celebrar ni que
 * cobrar. Con `umbralCentavos` (el aviso de cobranza es desde $1, `UMBRAL_AVISO_CENTAVOS`), un
 * monto distinto de 0 pero menor al umbral se muestra neutro, sin flecha: un "$0.39" vencido en
 * rojo junto a "Al corriente" es ruido. La comparación sigue siendo en centavos, tolerancia 0.
 *
 * Un negativo invierte la lectura: un ingreso negativo es dinero que salió (rojo, abajo) y un
 * adeudo negativo es saldo a favor del cliente (verde, arriba).
 *
 * Función pura (sin JSX) para poder probarla con vitest; el componente visual es
 * `components/ui/monto-tendencia`.
 */

export type SentidoMonto = 'ingreso' | 'adeudo' | 'neutro';

/** `arriba` = a favor, `abajo` = en contra, `null` = sin flecha. */
export type FlechaMonto = 'arriba' | 'abajo' | null;

/** Tono visual del monto. `cero` = gris; `vacio` = no hay monto; `neutro` = color normal del texto. */
export type TonoMonto = 'ingreso' | 'adeudo' | 'cero' | 'neutro' | 'vacio';

export interface EstiloMonto {
  flecha: FlechaMonto;
  tono: TonoMonto;
}

/** Centavos enteros: comparar dinero sin el ruido de los flotantes (tolerancia 0). */
export const centavosMonto = (n: number): number => Math.round(n * 100);

export function sentidoDeMonto(
  monto: number | null | undefined,
  sentido: SentidoMonto,
  umbralCentavos = 1,
): EstiloMonto {
  if (monto == null || !Number.isFinite(monto)) return { flecha: null, tono: 'vacio' };
  if (sentido === 'neutro') return { flecha: null, tono: 'neutro' };
  const cts = centavosMonto(monto);
  if (cts === 0) return { flecha: null, tono: 'cero' };
  if (Math.abs(cts) < umbralCentavos) return { flecha: null, tono: 'neutro' };
  const aFavor = sentido === 'ingreso' ? cts > 0 : cts < 0;
  return aFavor ? { flecha: 'arriba', tono: 'ingreso' } : { flecha: 'abajo', tono: 'adeudo' };
}

/** Clase de color (tokens del proyecto, respetan modo oscuro) de cada tono. */
export const CLASE_TONO_MONTO: Record<TonoMonto, string> = {
  ingreso: 'text-success',
  adeudo: 'text-danger',
  cero: 'text-muted-foreground',
  vacio: 'text-muted-foreground',
  neutro: '',
};

/** Prefijo que se lee en voz (lector de pantalla) antes del monto. */
export function prefijoLecturaMonto(estilo: EstiloMonto): string {
  if (estilo.tono === 'ingreso') return 'Ingreso de';
  if (estilo.tono === 'adeudo') return 'Adeudo de';
  return '';
}
